/**
 * One configuration row backed by a LIVE Bexio catalog (D3a).
 *
 * Its own component rather than another branch of `BexioSettingsControl`,
 * because a catalog control carries state that a plain input does not: the
 * options arrive over the network, the load can fail, and the stored value may
 * not be in the list that comes back. Folding four states into the general
 * control would make every field pay for what one field needs.
 *
 * THE STORED VALUE IS NEVER OVERRULED BY THE CATALOG. That is the whole design:
 *
 * - While the catalog loads, or after it fails, the row still shows the stored
 *   id. It does not go blank and it does not fall back to another option.
 * - If the catalog comes back WITHOUT the stored id, that id is rendered as its
 *   own marked entry rather than dropped. A currency that no longer exists in
 *   Bexio is a fact the operator has to see, not one the browser may quietly fix.
 * - Nothing is ever auto-selected — not even when the catalog holds exactly one
 *   option. That is the same rule the Bexio mapping dialogs follow
 *   (`BexioInvoiceMappingModal`: "never preselect (no auto-suggestion)").
 *
 * The label is display only. `value` is the integer the config stores, so a save
 * sends `currency_id: 2` and never `"EUR"`.
 */
import React from "react";

import { normalizeCatalogId } from "../bexioSettingsView";

const CONTROL =
  "w-full min-w-0 rounded-lg border border-input bg-background px-2.5 py-1.5 " +
  "text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 " +
  "focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60";

/** The stored value, as an option, when the catalog does not account for it. */
function orphanOption(value, catalogState) {
  if (value === null || value === undefined) return null;
  if (catalogState === "loaded") {
    return { value, label: `ID ${value} — nicht im Katalog` };
  }
  // Still loading, or the load failed: the id is all we can honestly show.
  return { value, label: `ID ${value}` };
}

export default function BexioCatalogSelect({
  id,
  label,
  hint,
  value = null,
  options = [],
  catalogState = "idle",
  catalogError = "",
  onRetry,
  missing = false,
  disabled = false,
  onChange,
  // D3f.2. An optional slot under the select, for a row that owns its own save
  // (the internal contact). A slot rather than a `showSaveButton` flag: this
  // component knows nothing about who saves what, and five of the six rows have
  // no action at all — a flag would put that knowledge in the wrong file.
  action = null,
}) {
  const known = options.some((o) => o.value === value);
  const orphan = known ? null : orphanOption(value, catalogState);
  const describedBy =
    [catalogError ? `${id}-error` : null, hint ? `${id}-hint` : null]
      .filter(Boolean)
      .join(" ") || undefined;

  return (
    <div className="min-w-0 border-b border-border py-3 last:border-b-0">
      <dt className="text-xs font-medium text-muted-foreground">
        <label htmlFor={id}>{label}</label>
        {missing ? (
          <span className="ml-2 whitespace-nowrap text-xs font-medium text-amber-800 dark:text-amber-300">
            fehlt
          </span>
        ) : null}
      </dt>
      <dd className="mt-1 min-w-0">
        <select
          id={id}
          value={value === null || value === undefined ? "" : String(value)}
          disabled={disabled}
          aria-describedby={describedBy}
          aria-busy={catalogState === "loading" ? true : undefined}
          className={CONTROL}
          onChange={(e) => onChange(normalizeCatalogId(e.target.value))}
        >
          <option value="">Nicht konfiguriert</option>
          {/* The stored value keeps its place in the list even when the catalog
              cannot account for it, so selecting it is never necessary to keep
              it — and losing it is never a side effect of a failed load. */}
          {orphan ? (
            <option value={String(orphan.value)}>{orphan.label}</option>
          ) : null}
          {options.map((o) => (
            <option key={o.value} value={String(o.value)}>
              {o.label}
            </option>
          ))}
        </select>

        {action ? <div className="mt-2">{action}</div> : null}

        {catalogState === "loading" ? (
          <span className="mt-1 block text-xs text-muted-foreground">
            Auswahlliste wird geladen …
          </span>
        ) : null}

        {catalogError ? (
          <span
            id={`${id}-error`}
            className="mt-1 flex flex-wrap items-center gap-2 text-xs text-amber-800 dark:text-amber-300"
          >
            <span className="min-w-0">{catalogError}</span>
            {onRetry ? (
              <button
                type="button"
                onClick={onRetry}
                className="rounded-md border border-input bg-background px-2 py-0.5 text-xs font-medium text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                Erneut versuchen
              </button>
            ) : null}
          </span>
        ) : null}

        {hint ? (
          <span id={`${id}-hint`} className="mt-0.5 block text-xs text-muted-foreground">
            {hint}
          </span>
        ) : null}
      </dd>
    </div>
  );
}
